import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { SUPPORT_MAILTO, SUPPORT_NAME } from '../../config/support';

// Add keyframes and styles for the exact reference design
const loginPageStyles = `
@import url('https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&display=swap');

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes glow {
  0%, 100% {
    opacity: 0.6;
    box-shadow: 0 0 10px rgba(76, 187, 184, 0.5);
  }
  50% {
    opacity: 1;
    box-shadow: 0 0 20px rgba(76, 187, 184, 0.8), 0 0 30px rgba(76, 187, 184, 0.6);
  }
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 10px rgba(76, 187, 184, 0.5);
  }
  50% {
    box-shadow: 0 0 25px rgba(76, 187, 184, 0.9), 0 0 40px rgba(76, 187, 184, 0.7);
  }
}



@keyframes typewriter {
  0% {
    width: 0;
  }
  100% {
    width: 100%;
  }
}

@keyframes cursor-blink {
  0%, 50% {
    border-right: 2px solid #e7550f;
  }
  51%, 100% {
    border-right: 2px solid transparent;
  }
}

.login-page-bg {
  font-family: 'Barlow', sans-serif;
  background: url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=2070') center/cover no-repeat;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
}

.login-page-bg::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(15, 38, 59, 0.95), rgba(15, 38, 59, 0.80));
  z-index: 1;
}


.login-content {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  transform: translateY(-10%);
}

.brand-logo {
  position: absolute;
  top: 2rem;
  left: 2rem;
  z-index: 20;
  transition: transform 1s ease-in-out;
}

.brand-logo.animate {
  animation: slideLogoLeft 1s ease-in-out forwards;
}

.brand-name {
  font-size: 1.725rem;
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 0.25rem;
}

.brand-subtitle {
  font-size: 1.006rem;
  color: #4cbbb8;
  font-weight: 500;
}

.typewriter-container {
  position: absolute;
  top: 2rem;
  left: 2rem;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.central-typewriter-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  margin-left: 1.5rem;
  margin-top: 1rem;
}

.typewriter-line {
  font-size: 1.006rem;
  color: #e7550f;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  border-right: 2px solid #e7550f;
  animation: blink 1s infinite;
}

.typewriter-line.complete {
  border-right: none;
}

.central-typewriter-line {
  font-size: 2.5rem;
  color: #e7550f;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  border-right: 2px solid #e7550f;
  text-align: center;
}

.central-typewriter-line.complete {
  border-right: none;
}

.typewriter-slogan-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.login-tagline {
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 400;
  letter-spacing: 0.02em;
  animation: fadeSlideUp 0.8s ease-out both;
}

@keyframes fadeSlideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.typewriter-slogan-line {
  font-size: 3.2rem;
  color: #ffffff;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-align: center;
  line-height: 1.2;
  position: relative;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}



.hg-logo {
  width: 208px;
  height: auto;
  margin-bottom: 2.53rem;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));
  transition: transform 1s ease-in-out;
}



.main-headline {
  font-size: 3.2rem;
  font-weight: bold;
  color: #ffffff;
  text-align: center;
  margin-bottom: 2.2rem;
  line-height: 1.2;
}

.login-glass-card {
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 20px;
  padding: 2.5rem 3rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  min-width: 360px;
  transform: translateY(3vh);
}

.login-glass-title {
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.login-glass-subtitle {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.login-help {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8125rem;
  margin-top: 0.75rem;
}

.login-help a {
  color: rgba(255, 255, 255, 0.9);
  text-decoration: underline;
}

.login-button {
  background: #ffffff;
  color: #0f263b;
  border: none;
  border-radius: 50px;
  padding: 0.875rem 2.5rem;
  font-size: 1rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 100%;
  justify-content: center;
}

.login-button:hover {
  background: #0f263b;
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(15, 38, 59, 0.4);
}

.microsoft-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.sub-text {
  color: white;
  font-size: 0.875rem;
  text-align: center;
  margin-top: 1rem;
  opacity: 0.9;
}

.footer-text {
  position: absolute;
  bottom: 3rem;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8625rem;
  z-index: 10;
}

/* Phones. Everything above assumes a tall, wide window: the page cannot
   scroll, the title does not wrap, and the footer is pinned to the bottom.
   Held sideways, a phone pushed the login button off-screen with no way to
   reach it. Here the page scrolls and the type scales to the screen. */
@media (max-width: 640px), (max-height: 500px) {
  .login-page-bg {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  /* The navy tint has to stay put while the content scrolls under it. */
  .login-page-bg::before {
    position: fixed;
  }
  .login-content {
    transform: none;
    min-height: calc(100dvh - 3rem);
    padding: 1.5rem 1rem;
  }
  .hg-logo {
    width: 150px;
    margin-bottom: 1.5rem;
  }
  .typewriter-slogan-line {
    font-size: clamp(1.75rem, 8vw, 3.2rem);
  }
  .login-tagline {
    font-size: 0.95rem;
    text-align: center;
  }
  .login-glass-card {
    min-width: 0;
    width: min(100%, 360px);
    padding: 2rem 1.25rem 1.5rem;
    transform: none;
  }
  .footer-text {
    position: relative;
    bottom: auto;
    left: auto;
    transform: none;
    text-align: center;
    height: 3rem;
    padding: 0 1rem;
  }
}

@media (max-height: 500px) {
  .hg-logo {
    width: 96px;
    margin-bottom: 0.75rem;
  }
  .typewriter-slogan-container {
    margin-bottom: 0.5rem;
  }
}
`;

// Immediately inject styles
const injectStyles = () => {
  const styleId = 'login-page-styles';
  if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = loginPageStyles;
    document.head.appendChild(style);
  }
};

// Call immediately
injectStyles();


export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { signInWithAzure, user, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Animation states
  const [typewriterText, setTypewriterText] = useState('');
  const [showTypewriter, setShowTypewriter] = useState(false);
  const [showSubtitle, setShowSubtitle] = useState(false);

  // Add debug logging for authentication state
  useEffect(() => {
    console.log('🔄 Login component - Auth state changed:', {
      hasUser: !!user,
      userEmail: user?.email,
      loading,
      isLoading: isLoading
    });

    if (user && !loading) {
      console.log('✅ Login component - User is authenticated, redirecting');
      console.log('👤 Authenticated user:', user.email);
      const intended = sessionStorage.getItem('redirectAfterLogin');
      sessionStorage.removeItem('redirectAfterLogin');
      navigate(intended && intended !== '/login' ? intended : '/', { replace: true });
    }
  }, [user, loading, navigate, isLoading]);

  // Clear corrupted session/storage data on login page mount
  useEffect(() => {
    const clearCorruptedData = async () => {
      try {
        console.log('🧹 Login: Cleaning up potentially corrupted storage data...');
        
        // Clear Supabase auth session
        await supabase.auth.signOut();
        
        // More aggressive localStorage cleaning
        console.log('🧹 Login: Current localStorage keys:', Object.keys(localStorage));
        
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (
            key.includes('supabase') ||
            key.includes('auth') ||
            key.includes('sb-') ||
            key.includes('userIsAdmin') ||
            key.includes('tqmapdzcaqbrkhlrcgxb')
          )) {
            keysToRemove.push(key);
          }
        }
        
        console.log('🧹 Login: Keys to remove:', keysToRemove);
        keysToRemove.forEach(key => {
          console.log('🧹 Login: Removing localStorage key:', key);
          localStorage.removeItem(key);
        });
        
        // Also clear any potential IndexedDB entries
        if ('indexedDB' in window) {
          try {
            indexedDB.deleteDatabase('supabase-js-auth');
            console.log('🧹 Login: Clearing IndexedDB auth database...');
          } catch (idbError) {
            console.warn('🧹 Login: Could not clear IndexedDB:', idbError);
          }
        }
        
        // Clear sessionStorage
        const sessionKeysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && (
            key.includes('supabase') ||
            key.includes('auth') ||
            key.includes('sb-') ||
            key.includes('showHeader')
          )) {
            sessionKeysToRemove.push(key);
          }
        }
        
        sessionKeysToRemove.forEach(key => {
          console.log('🧹 Login: Removing sessionStorage key:', key);
          sessionStorage.removeItem(key);
        });
        
        console.log('✅ Login: Storage cleanup completed');
        
      } catch (error) {
        console.warn('⚠️ Login: Error during storage cleanup:', error);
      }
    };
    
    clearCorruptedData();
  }, []);

  // Typewriter animation effect - start immediately
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTypewriter(true);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Typewriter animation effect
  useEffect(() => {
    if (!showTypewriter) return;
    
    const fullText = 'TRAINING PLATFORM';
    const currentCharIndex = typewriterText.length;
    
    if (currentCharIndex < fullText.length) {
      const timer = setTimeout(() => {
        setTypewriterText(fullText.substring(0, currentCharIndex + 1));
      }, 120);
      return () => clearTimeout(timer);
    } else if (currentCharIndex === fullText.length && !showSubtitle) {
      const timer = setTimeout(() => setShowSubtitle(true), 400);
      return () => clearTimeout(timer);
    }
  }, [showTypewriter, typewriterText, showSubtitle]);

  const handleAzureSignIn = async () => {
    setError(null);
    setIsLoading(true);

    try {
      console.log('🚀 Login: Starting Azure sign-in process...');
      console.log('🔍 Login: Current window location:', window.location.href);
      
      await signInWithAzure();
      // The OAuth flow will handle the redirect, and when we return,
      // the useEffect above will handle navigation if authenticated
    } catch (err) {
      console.error('❌ Login: Azure sign-in error:', err);
      
      // Check if it's a storage/session related error
      if (err instanceof Error && (
        err.message.includes('session') ||
        err.message.includes('storage') ||
        err.message.includes('localStorage')
      )) {
        setError('Login issue detected. Please try refreshing the page or clearing browser data.');
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred during Azure sign-in');
      }
      setIsLoading(false);
    }
  };

  const handleClearStorageAndRetry = async () => {
    try {
      console.log('🧹 Manual storage cleanup requested...');
      
      // Clear everything
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear Supabase session
      await supabase.auth.signOut();
      
      console.log('✅ Manual storage cleanup completed');
      
      // Reload the page to start fresh
      window.location.reload();
    } catch (error) {
      console.error('❌ Manual cleanup error:', error);
    }
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center login-page-bg">
        <div className="text-white/80">Loading...</div>
      </div>
    );
  }

  // Don't show login form if already authenticated
  if (user) {
    console.log('User already authenticated, component will unmount');
    return null;
  }

  return (
    <div className="login-page-bg">
       <div className="login-content">
         <img
           src="https://tqmapdzcaqbrkhlrcgxb.supabase.co/storage/v1/object/public/quiz-media/appmedia/HGC.png"
           alt="HG Construction Logo"
           className="hg-logo"
         />

         {showTypewriter && (
           <div className="typewriter-slogan-container">
             <div className="typewriter-slogan-line">
               {typewriterText}
             </div>
             {showSubtitle && (
               <div className="login-tagline">
                 A central hub for internal training and knowledge.
               </div>
             )}
           </div>
         )}

        <div className="login-glass-card">
          <div className="login-glass-title">Secure Login</div>
          <div className="login-glass-subtitle">Enter your enterprise credentials.</div>
          <button
            className="login-button"
            onClick={handleAzureSignIn}
            disabled={isLoading}
          >
            <img
              src="https://tqmapdzcaqbrkhlrcgxb.supabase.co/storage/v1/object/public/quiz-media/appmedia/MicrosoftLogo.png"
              alt="Microsoft"
              className="microsoft-icon"
            />
            {isLoading ? 'Signing in...' : 'Log in with Microsoft'}
          </button>
          <div className="login-help">
            Trouble signing in? Email <a href={SUPPORT_MAILTO}>{SUPPORT_NAME}</a>
          </div>
        </div>

        {error && (
          <div className="mt-4 text-red-400 text-sm text-center">
            <div className="mb-2">{error}</div>
            {error.includes('Login issue detected') && (
              <button
                onClick={handleClearStorageAndRetry}
                className="text-blue-400 underline text-xs hover:text-blue-300"
              >
                Click here to clear browser data and retry
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="footer-text">
        Developed in-house by Shamel Ali
      </div>
    </div>
  );
}; 